import { useEffect, useState } from "react";
import { Volume2, Check, X } from "lucide-react";
import { http, formatApiError } from "@/lib/api";
import { speak } from "@/lib/buddy";
import { toast } from "sonner";

const say = (text, voice) => speak(text, voice).catch(() => {});

export const StyleQuiz = ({ quiz, interests, readAloud, voice, onDone }) => {
  const [i, setI] = useState(0);
  const [ans, setAns] = useState({});
  const [picks, setPicks] = useState([]);
  const q = quiz[i];
  useEffect(() => { if (readAloud && q) say(`${q.q} ${q.options.map((o) => o[1]).join(". ")}`, voice); }, [i]); // eslint-disable-line react-hooks/exhaustive-deps
  const choose = (v) => { setAns({ ...ans, [q.id]: v }); setI(i + 1); };
  const submit = async () => {
    try { onDone((await http.post("/assessment/style", { answers: ans, interests: picks })).data); } catch (e) { toast.error(formatApiError(e)); }
  };
  if (q) return (
    <div className="rise" data-testid={`style-q-${q.id}`} key={q.id}>
      <p className="text-xs uppercase tracking-widest text-stone-500">How I learn · {i + 1} of {quiz.length + 1}</p>
      <div className="flex items-start gap-3 mt-3">
        <h2 className="font-display text-3xl text-[#2C4A3E] flex-1">{q.q}</h2>
        <button onClick={() => say(`${q.q} ${q.options.map((o) => o[1]).join(". ")}`, voice)} data-testid="style-read-btn" aria-label="Read aloud" className="p-2 rounded-full bg-[#F1EADB]"><Volume2 className="w-5 h-5" /></button>
      </div>
      <div className="grid sm:grid-cols-2 gap-3 mt-6">{q.options.map(([v, l]) => (
        <button key={v} onClick={() => choose(v)} data-testid={`style-opt-${q.id}-${v}`} className="text-left rounded-2xl border border-[#E6DEC3] bg-white px-5 py-4 hover:border-[#2C4A3E] hover:-translate-y-0.5 transition-[transform,border-color]">{l}</button>
      ))}</div>
    </div>
  );
  return (
    <div className="rise" data-testid="style-interests">
      <p className="text-xs uppercase tracking-widest text-stone-500">How I learn · {quiz.length + 1} of {quiz.length + 1}</p>
      <h2 className="font-display text-3xl text-[#2C4A3E] mt-3">What do you love? Pick up to 6</h2>
      <div className="flex flex-wrap gap-2 mt-6">{interests.map((t) => (
        <button key={t} onClick={() => setPicks(picks.includes(t) ? picks.filter((x) => x !== t) : picks.length < 6 ? [...picks, t] : picks)} data-testid={`interest-${t.replace(/\W+/g, "-")}`}
          className={`px-4 py-2 rounded-full border text-sm transition-colors ${picks.includes(t) ? "bg-[#2C4A3E] text-white border-[#2C4A3E]" : "bg-white border-[#E6DEC3]"}`}>{t}</button>
      ))}</div>
      <button onClick={submit} data-testid="style-submit-btn" className="mt-8 px-7 py-3 rounded-full bg-[#C87D53] text-white">Next: skill check</button>
    </div>
  );
};

export const SkillCheck = ({ sid, name, readAloud, voice, onDone }) => {
  const [d, setD] = useState(null);
  const [fb, setFb] = useState(null);
  const [err, setErr] = useState("");
  const load = () => { setErr(""); setD(null); http.get(`/assessment/subject/${sid}/next`).then((r) => (r.data.done ? onDone() : setD(r.data))).catch((e) => setErr(formatApiError(e))); };
  useEffect(load, [sid]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (readAloud && d?.question) say(`${d.question.q} ${d.question.choices.join(". ")}`, voice); }, [d?.question?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const pick = async (i) => {
    try {
      const r = await http.post(`/assessment/subject/${sid}/answer`, { qid: d.question.id, choice: i });
      setFb({ i, ok: r.data.correct });
      setTimeout(() => { setFb(null); r.data.done ? onDone() : load(); }, 900);
    } catch (e) { toast.error(formatApiError(e)); load(); }
  };
  if (err) return <div data-testid="skill-error"><p className="text-stone-600">{err}</p><button onClick={load} data-testid="skill-retry" className="mt-4 px-5 py-2 rounded-full bg-[#2C4A3E] text-white">Try again</button></div>;
  if (!d) return <p className="text-stone-500 font-display text-xl" data-testid="skill-loading">Getting your {name} questions ready…</p>;
  const q = d.question;
  return (
    <div className="rise" key={q.id} data-testid={`skill-q-${sid}`}>
      <p className="text-xs uppercase tracking-widest text-[#C87D53]">{name} · question {d.number} of {d.total}</p>
      <div className="flex items-start gap-3 mt-3">
        <h2 className="font-display text-3xl text-[#2C4A3E] flex-1" data-testid="skill-question">{q.q}</h2>
        <button onClick={() => say(`${q.q} ${q.choices.join(". ")}`, voice)} data-testid="skill-read-btn" aria-label="Read aloud" className="p-2 rounded-full bg-[#F1EADB]"><Volume2 className="w-5 h-5" /></button>
      </div>
      <div className="grid sm:grid-cols-2 gap-3 mt-6">{q.choices.map((c, i) => (
        <button key={i} disabled={!!fb} onClick={() => pick(i)} data-testid={`skill-choice-${i}`}
          className={`text-left rounded-2xl border px-5 py-4 inline-flex items-center gap-3 transition-colors ${fb?.i === i ? (fb.ok ? "bg-[#E3EBE5] border-[#6B9080]" : "bg-[#FBEAEA] border-red-300") : "bg-white border-[#E6DEC3] hover:border-[#2C4A3E]"}`}>
          {fb?.i === i && (fb.ok ? <Check className="w-4 h-4 text-[#6B9080]" /> : <X className="w-4 h-4 text-red-600" />)}{c}
        </button>
      ))}</div>
      <p className="text-xs text-stone-500 mt-5">It's okay not to know. Questions get easier or harder to find just the right fit for you.</p>
    </div>
  );
};
